import { inject, Injectable } from '@angular/core';
import { dsDtos } from '@vectronic/openApi-admin';
import { ScheduleHelp } from '@vectronic/util';
import { ScheduleFieldRow, ScheduleFormatService, SequenceGroup } from './schedule-format.service';

export interface ScheduleCompareCollar {
  id: number;
  label: string;
  schedules: dsDtos.Schedule[];
}

export interface RuleGroup {
  label: string;
  fields: ScheduleFieldRow[];
  sequences: SequenceGroup[];
  isExpanded: boolean;
}

export interface ScheduleTypeGroup {
  scheduleType: string;
  label: string;
  rules: RuleGroup[];
}

@Injectable({ providedIn: 'root' })
export class ScheduleCompareService {
  private readonly scheduleFormat = inject(ScheduleFormatService);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  buildScheduleTypeGroups(collars: ScheduleCompareCollar[]): ScheduleTypeGroup[] {
    if (!collars.length) return [];

    const allTypes = new Set<string>();
    for (const collar of collars) {
      for (const schedule of collar.schedules) {
        if (schedule.scheduleType) allTypes.add(schedule.scheduleType);
      }
    }

    const groups: ScheduleTypeGroup[] = [];

    for (const scheduleType of allTypes) {
      const collarSchedules = collars.map(
        (c) =>
          c.schedules.find((s) => s.scheduleType === scheduleType && s.userDefined) ??
          c.schedules.find((s) => s.scheduleType === scheduleType),
      );

      const isCamera = scheduleType === 'CAMERA';
      const rules: RuleGroup[] = [];

      if (isCamera) {
        const maxRules = Math.max(
          ...collarSchedules.map((s) => s?.cameraScheduleRules?.length ?? 0),
          0,
        );
        for (let i = 0; i < maxRules; i++) {
          const collarRules = collarSchedules.map((s) => s?.cameraScheduleRules?.[i]);
          rules.push({
            label: `Rule ${i + 1}`,
            fields: this.scheduleFormat.buildCameraRuleFields(collarRules),
            sequences: [],
            isExpanded: true,
          });
        }
      } else {
        const maxRules = Math.max(...collarSchedules.map((s) => s?.rules?.length ?? 0), 0);
        for (let i = 0; i < maxRules; i++) {
          const collarRules = collarSchedules.map((s) => s?.rules?.[i]);
          rules.push({
            label: `Rule ${i + 1}`,
            fields: this.scheduleFormat.buildRuleFields(collarRules),
            sequences: this.scheduleFormat.buildSequenceGroups(
              collarRules,
              collars.length,
              scheduleType === 'COMMUNICATION',
            ),
            isExpanded: true,
          });
        }
      }

      groups.push({
        scheduleType,
        label:
          ScheduleHelp.toLowerCase(
            scheduleType as Parameters<typeof ScheduleHelp.toLowerCase>[0],
          ) ?? scheduleType,
        rules,
      });
    }

    return groups
      .map((group) => ({ ...group, rules: this.filterEmptyRules(group.rules) }))
      .filter((group) => !!group.rules?.length);
  }

  private isFieldEmpty(field: ScheduleFieldRow): boolean {
    return !field.values?.length || field.values?.every((v) => v === '—');
  }

  private filterEmptyRules(rules: RuleGroup[]): RuleGroup[] {
    return rules
      .map((rule) => ({
        ...rule,
        fields: rule.fields?.filter((f) => !this.isFieldEmpty(f)),
        sequences: rule.sequences
          ?.map((seq) => ({ ...seq, fields: seq.fields?.filter((f) => !this.isFieldEmpty(f)) }))
          .filter((seq) => !!seq.fields?.length),
      }))
      ?.filter((rule) => !!rule.fields?.length || !!rule.sequences?.length);
  }
}
